import { makeAutoObservable } from 'mobx';
import axios, { CancelTokenSource } from "axios";

import { CacheController } from "../Controllers/CacheController";
import { utils } from "../Controllers/Utils";

interface Constructable<T> {
  new(data: any): T;
}

type IsArray<T> = T extends (infer U)[] ? U : never;

export class AjaxController {
  private cacheController = new CacheController();
  baseUrl = utils.apiBaseUrl

  private cancelTokenSources: { [key: string]: CancelTokenSource } = {};

  private jwtInterceptorId: number | null = null;

  setJwtToken(token: string) {
    if (this.jwtInterceptorId != null) {
      axios.interceptors.request.eject(this.jwtInterceptorId);
    }

    this.jwtInterceptorId = axios.interceptors.request.use(config => {
      config.headers['Authorization'] = `Bearer ${token}`;
      return config;
    });
  }

  async get<T>(
    endpoint: string,
    params: object,
    Type: Constructable<IsArray<T> extends never ? T : IsArray<T>>
  ): Promise<T> {
    try {
      // TODO: might want to add local cache in the future
      // Check if the cache has the data for the given endpoint
      // const cachedData = this.cacheController.get<T>(endpoint);
      // if (cachedData != null) {
      //   return this.instantiate(Type, cachedData);
      // }
      const url = `${this.baseUrl}/${endpoint}`;
      const cancelKey = endpoint;
      const newToken = axios.CancelToken.source();

      this.cancelPreviousRequest(cancelKey);
      this.cancelTokenSources[cancelKey] = newToken;

      const result = await axios.get(url, {
        params,
        cancelToken: newToken.token
      });

      return this.instantiate(Type, result.data);
    } catch (error) {
      if (axios.isCancel(error)) return Promise.reject("CANCELLED");
      throw error;
    }
  }

  async getNoCast<T>(endpoint: string, params: object): Promise<T> {
    try {
      const url = `${this.baseUrl}/${endpoint}`;
      const parts = endpoint.split("/");
      const cancelKey = parts.slice(0, 2).join("/");
      const newToken = axios.CancelToken.source();

      this.cancelPreviousRequest(cancelKey);
      this.cancelTokenSources[cancelKey] = newToken;

      const result = await axios.get<T>(url, {
        params,
        cancelToken: newToken.token
      });

      return result.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        return Promise.reject('CANCELLED');
      }
      throw new Error("An error occured");
    }
  }

  async post<T, T1>(endpoint: string, data: T, Type: Constructable<IsArray<T1> extends never ? T1 : IsArray<T1>>): Promise<T1> {
    try {
      const result = await axios.post<T>(`${this.baseUrl}/${endpoint}`, data);
      return this.instantiate(Type, result.data);
    } catch (error) {
      throw new Error("An error occured")
    }
  }

  async postNoCast<T, T1>(endpoint: string, data: T): Promise<T1> {
    try {
      const result = await axios.post<T>(`${this.baseUrl}/${endpoint}`, data);
      return result.data;
    } catch (error) {
      throw new Error("An error occured")
    }
  }

  async delete(endpoint: string): Promise<void> {
    try {
      const result = await axios.delete<void>(`${this.baseUrl}/${endpoint}`);
      return result.data;
    } catch (error) {
      throw new Error("An error occured")
    }
  }

  async downloadFile(endpoint: string, filename: string): Promise<void> {
    try {
      const url = `${this.baseUrl}/${endpoint}`;
      const response = await axios.get(url, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
      throw new Error('Failed to download file');
    }
  }

  private instantiate<T>(Type: Constructable<IsArray<T> extends never ? T : IsArray<T>>, data: any): T {
    if (Array.isArray(data)) {
      // If T is already an array, we're assuming IsArray<T> gives us the inner type
      return data.map(item => new Type(item)) as unknown as T;
    }
    return new Type(data) as T;
  }

  private cancelPreviousRequest(endpoint: string) {
    if (this.cancelTokenSources[endpoint]) {
      this.cancelTokenSources[endpoint].cancel('Request cancelled due to a new request.');
    }
  }

  private createNewCancelToken(endpoint: string): CancelTokenSource {
    this.cancelTokenSources[endpoint] = axios.CancelToken.source();
    return this.cancelTokenSources[endpoint];
  }

  private getCancelKey(url: string, params: any) {
    return url + "?" + new URLSearchParams(params as any).toString();
  }

  constructor() {
    makeAutoObservable(this);
  }
}

export const ajaxController = new AjaxController();